import React from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "../../lib/ThemeContext";
import { API_BASE, kcFetch, authHeaders, getToken } from "../../lib/apiClient";

// ============================================================
// DarkModePopup — Phase-4 smart nudge.
// ============================================================
// Shown ONLY when:
//   • The user is in light mode.
//   • The current route is one of the "dark-native" pages
//     (Dashboard + Tree View).
//   • The user has NOT permanently dismissed it via the
//     "Don't show again" checkbox (server-side flag stored on
//     the user's preferences).
//   • It hasn't been dismissed in the current visit already.
//
// Delay: appears 1.5 s after the page mounts.
// Entrance: slide-up from bottom + fade, 0.3 s ease.
// ============================================================

// Route prefixes where the popup is allowed to appear.
const DARK_NATIVE_ROUTES = ["/dashboard", "/followuptree", "/followup-tree"];

// Session-scoped dismissal (resets on page reload). Kept out of
// component state so navigating between the two dark-native pages
// during a single visit doesn't re-trigger the popup after a
// dismissal.
let VISIT_DISMISSED = false;

export function DarkModePopup({ currentUser }) {
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const path = location.pathname;
  const [visible, setVisible] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  const permanentlyDismissed = !!(
    currentUser?.preferences?.dark_mode_popup_dismissed_permanently
  );

  const onDarkNativeRoute = DARK_NATIVE_ROUTES.some(
    (p) => path === p || path.startsWith(p + "/")
  );

  // Eligibility gate — recompute whenever inputs change.
  const eligible =
    theme === "light" &&
    onDarkNativeRoute &&
    !permanentlyDismissed &&
    !VISIT_DISMISSED &&
    !!currentUser;

  React.useEffect(() => {
    if (!eligible) {
      setVisible(false);
      return () => {};
    }
    // 1.5 s delay after mount, per spec.
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [eligible, path]);

  const persistPermanentDismissal = React.useCallback(async () => {
    if (!getToken()) return;
    try {
      await kcFetch(`${API_BASE}/me/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ dark_mode_popup_dismissed_permanently: true }),
      });
    } catch (_) { /* offline-tolerant */ }
  }, []);

  const closeForVisit = () => {
    VISIT_DISMISSED = true;
    setVisible(false);
  };
  const closeAll = async (persistIfChecked) => {
    if (persistIfChecked && dontShowAgain) {
      await persistPermanentDismissal();
    }
    closeForVisit();
  };
  const switchToDark = async () => {
    setTheme("dark");
    if (dontShowAgain) await persistPermanentDismissal();
    closeForVisit();
  };

  if (!eligible) return null;
  return (
    <div
      className={`kc-darkmode-popup ${visible ? "is-open" : ""}`}
      data-testid="dark-mode-popup"
      role="dialog"
      aria-label="Try dark mode"
    >
      <button
        type="button"
        className="kc-darkmode-popup-close"
        data-testid="dark-mode-popup-close"
        aria-label="Dismiss"
        onClick={() => closeAll(true)}
      >
        ×
      </button>
      <div className="kc-darkmode-popup-icon" aria-hidden="true">
        {/* Moon */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
        </svg>
      </div>
      <div className="kc-darkmode-popup-body">
        <div className="kc-darkmode-popup-title">Try Dark Mode for this page</div>
        <div className="kc-darkmode-popup-text">
          The Dashboard and Tree View are designed for dark mode. Switch for the full Kingdom Connect experience.
        </div>
        <div className="kc-darkmode-popup-actions">
          <button
            type="button"
            className="kc-darkmode-popup-primary"
            data-testid="dark-mode-popup-switch"
            onClick={switchToDark}
          >
            Switch to Dark
          </button>
          <button
            type="button"
            className="kc-darkmode-popup-secondary"
            data-testid="dark-mode-popup-keep"
            onClick={() => closeAll(true)}
          >
            Keep Light
          </button>
        </div>
        <label className="kc-darkmode-popup-checkbox">
          <input
            type="checkbox"
            data-testid="dark-mode-popup-dontshow"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <span>Don&apos;t show again</span>
        </label>
      </div>
    </div>
  );
}

export default DarkModePopup;
